import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import '@radix-ui/themes/styles.css'

// Pages
import Login from './pages/Login'
import SalesRegister from './pages/SalesRegister'
import Dashboard from './pages/Dashboard'
import Customers from './pages/Customers'
import CustomerDetail from './pages/CustomerDetail'
import Products from './pages/Products'
import ProductDetail from './pages/ProductDetail'
import PriceManager from './pages/PriceManager'
import PriceBook from './pages/PriceBook'
import InventoryLifecycle from './pages/InventoryLifecycle'
import Invoices from './pages/Invoices'
import InvoiceDetail from './pages/InvoiceDetail'
import Estimates from './pages/Estimates'
import EstimateDetail from './pages/EstimateDetail'
import EstimateEditor from './pages/EstimateEditor'
import Bills from './pages/Bills'
import BillDetail from './pages/BillDetail'
import BillEditor from './pages/BillEditor'
import Appointments from './pages/Appointments'
import AppointmentDetail from './pages/AppointmentDetail'
import Vendors from './pages/Vendors'
import Workers from './pages/Workers'
import Rolodex from './pages/Rolodex'
import GiftCards from './pages/GiftCards'
import Payouts from './pages/Payouts'
import Refunds from './pages/Refunds'
import Receiving from './pages/Receiving'
import InventoryPictures from './pages/InventoryPictures'
import LookupManager from './pages/LookupManager'
import Settings from './pages/Settings'
import Reports from './pages/Reports'
import Reconciliation from './pages/Reconciliation'
import AdminDashboard from './pages/AdminDashboard'
import NotFound from './pages/NotFound'
import SitePlans from './pages/SitePlans'
import SitePlanEditor from './pages/SitePlanEditor'

// Context
import { AuthProvider } from './context/AuthContext'
import { RoleProvider } from './context/RoleContext'

// Components
import ProtectedRoute from './components/shared/ProtectedRoute'
import Layout from './components/shared/Layout'
import HomeRedirect from './components/shared/HomeRedirect'

type Role = 'cashier' | 'manager' | 'admin'

function Protected({ roles, children }: { roles: Role[]; children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={roles}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  )
}

const ALL: Role[] = ['cashier', 'manager', 'admin']
const MGR: Role[] = ['manager', 'admin']
const ADM: Role[] = ['admin']

function App() {
  return (
    <AuthProvider>
      <RoleProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Point of Sale */}
            <Route path="/sales" element={<Protected roles={ALL}><SalesRegister /></Protected>} />
            <Route path="/pos" element={<Protected roles={ALL}><SalesRegister /></Protected>} />
            <Route path="/transactions" element={<Protected roles={ALL}><Invoices /></Protected>} />
            <Route path="/transactions/:id" element={<Protected roles={ALL}><InvoiceDetail /></Protected>} />
            <Route path="/invoices" element={<Protected roles={MGR}><Invoices /></Protected>} />
            <Route path="/invoices/:id" element={<Protected roles={MGR}><InvoiceDetail /></Protected>} />
            <Route path="/refunds" element={<Protected roles={MGR}><Refunds /></Protected>} />
            <Route path="/gift-cards" element={<Protected roles={ALL}><GiftCards /></Protected>} />
            <Route path="/payouts" element={<Protected roles={MGR}><Payouts /></Protected>} />

            {/* Customers & Jobs */}
            <Route path="/customers" element={<Protected roles={ALL}><Customers /></Protected>} />
            <Route path="/customers/:id" element={<Protected roles={ALL}><CustomerDetail /></Protected>} />
            <Route path="/appointments" element={<Protected roles={MGR}><Appointments /></Protected>} />
            <Route path="/appointments/:id" element={<Protected roles={MGR}><AppointmentDetail /></Protected>} />
            <Route path="/estimates" element={<Protected roles={MGR}><Estimates /></Protected>} />
            <Route path="/estimates/new" element={<Protected roles={MGR}><EstimateEditor /></Protected>} />
            <Route path="/estimates/:id/edit" element={<Protected roles={MGR}><EstimateEditor /></Protected>} />
            <Route path="/estimates/:id" element={<Protected roles={MGR}><EstimateDetail /></Protected>} />
            <Route path="/bills" element={<Protected roles={MGR}><Bills /></Protected>} />
            <Route path="/bills/new" element={<Protected roles={MGR}><BillEditor /></Protected>} />
            <Route path="/bills/:id/edit" element={<Protected roles={MGR}><BillEditor /></Protected>} />
            <Route path="/bills/:id" element={<Protected roles={MGR}><BillDetail /></Protected>} />
            <Route path="/workers" element={<Protected roles={MGR}><Workers /></Protected>} />
            <Route path="/rolodex" element={<Protected roles={MGR}><Rolodex /></Protected>} />
            <Route path="/site-plans" element={<Protected roles={MGR}><SitePlans /></Protected>} />
            <Route path="/site-plans/:id" element={<Protected roles={MGR}><SitePlanEditor /></Protected>} />

            {/* Inventory */}
            <Route path="/products" element={<Protected roles={MGR}><Products /></Protected>} />
            <Route path="/products/:id" element={<Protected roles={MGR}><ProductDetail /></Protected>} />
            <Route path="/price-manager" element={<Protected roles={MGR}><PriceManager /></Protected>} />
            <Route path="/price-book" element={<Protected roles={MGR}><PriceBook /></Protected>} />
            <Route path="/inventory-lifecycle" element={<Protected roles={MGR}><InventoryLifecycle /></Protected>} />
            <Route path="/inventory-pictures" element={<Protected roles={MGR}><InventoryPictures /></Protected>} />
            <Route path="/vendors" element={<Protected roles={MGR}><Vendors /></Protected>} />
            <Route path="/receiving" element={<Protected roles={MGR}><Receiving /></Protected>} />

            {/* Insights */}
            <Route path="/dashboard" element={<Protected roles={MGR}><Dashboard /></Protected>} />
            <Route path="/reports" element={<Protected roles={MGR}><Reports /></Protected>} />
            <Route path="/reconciliation" element={<Protected roles={MGR}><Reconciliation /></Protected>} />

            {/* Administration */}
            <Route path="/departments" element={<Protected roles={ADM}><LookupManager title="Departments" parentTable="departments" parentLabel="Department" childTable="sub_departments" childLabel="Sub-Department" childParentCol="department_id" /></Protected>} />
            <Route path="/categories" element={<Protected roles={ADM}><LookupManager title="Categories" parentTable="categories" parentLabel="Category" childTable="sub_categories" childLabel="Sub-Category" childParentCol="category_id" /></Protected>} />
            <Route path="/admin" element={<Protected roles={ADM}><AdminDashboard /></Protected>} />
            <Route path="/settings" element={<Protected roles={ADM}><Settings /></Protected>} />

            <Route path="/" element={<HomeRedirect />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <ToastContainer position="top-right" autoClose={4000} closeOnClick pauseOnHover theme="light" />
        </Router>
      </RoleProvider>
    </AuthProvider>
  )
}

export default App
